(() => {
  if (window.__ayuShoppableVideoReady) return;
  window.__ayuShoppableVideoReady = true;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1));
  const cartRoot = () => window.Shopify?.routes?.root || '/';
  const cartAddUrl = () => `${cartRoot()}cart/add.js`.replace(/([^:]\/)\/+/g, '$1');

  const findRoot = (element) => element?.closest?.('[data-ayu-shoppable-root], [data-ayu-floating-video]');

  const setVideoMuted = (video, muted = true) => {
    if (!video) return;
    video.muted = muted;
    if (muted) {
      video.setAttribute('muted', '');
    } else {
      video.removeAttribute('muted');
    }
  };

  const setAudioControls = (scope, muted = true) => {
    scope?.querySelectorAll?.('[data-ayu-video-audio-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', String(!muted));
      button.setAttribute('aria-label', muted ? button.dataset.labelMuted || 'Unmute video' : button.dataset.labelUnmuted || 'Mute video');
    });
  };

  const safePlay = (video, options = {}) => {
    if (!video || prefersReducedMotion()) return;
    const muted = options.muted !== false;
    setVideoMuted(video, muted);
    video.setAttribute('playsinline', '');
    if (video.readyState === 0) video.load();
    window.requestAnimationFrame(() => {
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          if (!muted) {
            setVideoMuted(video, true);
            setAudioControls(options.scope, true);
            video.play().catch(() => {});
          }
        });
      }
    });
  };

  const loadVideo = (host, shouldPlay = false, options = {}) => {
    if (!host) return null;
    if (host.dataset.videoLoaded === 'true') {
      const existing = host.querySelector('video');
      if (shouldPlay) safePlay(existing, options);
      return existing;
    }

    const template = host.querySelector('template[data-ayu-video-template]');
    const media = host.matches('[data-ayu-video-media]') ? host : host.querySelector('[data-ayu-video-media]') || host;
    if (!template || !media) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    const wantsPlayback = shouldPlay || host.dataset.autoplay === 'true';
    const muted = options.muted !== false;
    if (video) {
      setVideoMuted(video, muted);
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.autoplay = wantsPlayback;
      video.preload = shouldPlay ? 'auto' : wantsPlayback ? 'metadata' : host.dataset.preload || 'none';
      if (host.dataset.controls === 'true') video.controls = true;
      if (host.dataset.loop !== 'false') video.loop = true;
    }

    media.appendChild(fragment);
    host.dataset.videoLoaded = 'true';
    host.classList.add('is-video-loaded');

    if (video && wantsPlayback) safePlay(video, options);
    return video;
  };

  const primeImages = (scope) => {
    scope?.querySelectorAll?.('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
      image.fetchPriority = 'high';
      if (typeof image.decode === 'function') image.decode().catch(() => {});
    });
  };

  const pauseInactiveVideos = (root, activePanel = null) => {
    root?.querySelectorAll('video').forEach((video) => {
      if (activePanel && activePanel.contains(video)) return;
      video.pause();
    });
  };

  const muteVideos = (root) => {
    root?.querySelectorAll('video').forEach((video) => setVideoMuted(video, true));
    setAudioControls(root, true);
  };

  const resetAudioToggles = (button) => {
    document.querySelectorAll('[data-ayu-video-audio-toggle][aria-pressed="true"]').forEach((activeButton) => {
      if (activeButton === button) return;
      activeButton.setAttribute('aria-pressed', 'false');
      activeButton.setAttribute('aria-label', activeButton.dataset.labelMuted || 'Unmute video');
    });
  };

  const toggleAudio = (button) => {
    const scope = button.closest('[data-ayu-video-host], [data-ayu-video-panel], [data-ayu-video-card], [data-ayu-floating-video]');
    const host = scope?.matches('[data-ayu-video-host]') ? scope : scope?.querySelector('[data-ayu-video-host]');
    const video = loadVideo(host, true) || scope?.querySelector('video');
    if (!video) return;

    const shouldUnmute = video.muted;
    document.querySelectorAll('video').forEach((otherVideo) => {
      if (otherVideo !== video) setVideoMuted(otherVideo, true);
    });
    resetAudioToggles(button);
    setVideoMuted(video, !shouldUnmute);
    button.setAttribute('aria-pressed', String(shouldUnmute));
    button.setAttribute('aria-label', shouldUnmute ? button.dataset.labelUnmuted || 'Mute video' : button.dataset.labelMuted || 'Unmute video');
  };

  const activatePanel = (root, index, options = {}) => {
    const panels = [...root.querySelectorAll('[data-ayu-video-panel]')];
    if (!panels.length) return null;
    const nextIndex = (Number(index) + panels.length) % panels.length;
    root.dataset.activeIndex = String(nextIndex);

    panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === nextIndex;
      panel.hidden = !isActive;
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        primeImages(panel);
        const host = panel.querySelector('[data-ayu-video-host]');
        const muted = options.unmute === true ? false : options.muted !== false;
        const video = loadVideo(host, true, { muted, scope: panel });
        setAudioControls(panel, video ? video.muted : muted);
      }
    });

    const activePanel = panels[nextIndex];
    pauseInactiveVideos(root, activePanel);
    return activePanel;
  };

  const openModal = (root, index, options = {}) => {
    const dialog = root?.querySelector('[data-ayu-video-modal]');
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('ayu-video-modal-open');
    muteVideos(root);
    window.requestAnimationFrame(() => activatePanel(root, index, { unmute: options.unmute !== false }));
  };

  const closeModal = (dialog) => {
    if (!dialog) return;
    const root = findRoot(dialog);
    muteVideos(root);
    pauseInactiveVideos(root);
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    if (!document.querySelector('[data-ayu-video-modal][open]')) {
      document.documentElement.classList.remove('ayu-video-modal-open');
    }
  };

  const updateVariantState = (select) => {
    const option = select.selectedOptions[0];
    const product = select.closest('[data-ayu-video-product]');
    if (!option || !product) return;
    const available = option.dataset.available === 'true';
    const button = product.querySelector('[data-ayu-video-add]');
    const buyButton = product.querySelector('[data-ayu-video-buy-now]');
    const price = product.querySelector('[data-ayu-video-price]');
    const compare = product.querySelector('[data-ayu-video-compare]');

    if (price) price.textContent = option.dataset.price || '';
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
    if (button) {
      button.disabled = !available;
      button.textContent = available ? button.dataset.defaultLabel : button.dataset.soldOutLabel;
    }
    if (buyButton) buyButton.disabled = !available;
  };

  const buyNow = (button) => {
    const form = button.closest('.ayu-video-product__form, .ayu-video-card__form');
    if (!form || button.disabled) return;
    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    window.location.href = `${cartRoot()}cart/${variantId}:1`.replace(/([^:]\/)\/+/g, '$1');
  };

  const showStatus = (form, text, tone = 'success') => {
    const status = form.querySelector('[data-ayu-video-status]');
    if (!status) return;
    status.hidden = !text;
    status.textContent = text || '';
    status.dataset.tone = tone;
  };

  const addToCart = async (form) => {
    const button = form.querySelector('[data-ayu-video-add]');
    const defaultLabel = button?.dataset.defaultLabel || button?.textContent || 'Add';
    const formData = new FormData(form);

    button?.setAttribute('aria-busy', 'true');
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding...';
    }
    showStatus(form, '');

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.status) {
        throw new Error(payload?.description || payload?.message || 'Unable to add this item.');
      }

      if (window.AyuTheme?.incrementCartCount) window.AyuTheme.incrementCartCount(1);
      if (window.AyuTheme?.refreshCartCount) {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => window.AyuTheme.refreshCartCount());
        } else {
          window.setTimeout(() => window.AyuTheme.refreshCartCount(), 250);
        }
      }
      if (window.gokwik?.fetchCart) window.gokwik.fetchCart();
      window.AyuTheme?.emitAnalytics?.('add_to_cart', {
        product_id: form.closest('[data-ayu-video-product]')?.dataset.productId || '',
        product_name: form.closest('[data-ayu-video-product]')?.dataset.productTitle || '',
        source: 'shoppable_video',
      });
      showStatus(form, 'Added to cart', 'success');
      if (button) button.textContent = 'Added';
      window.setTimeout(() => {
        if (button && !button.disabled) button.textContent = defaultLabel;
      }, 1600);
    } catch (error) {
      showStatus(form, error.message || 'Unable to add this item.', 'error');
      if (button) button.textContent = defaultLabel;
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
      const select = form.querySelector('[data-ayu-video-variant-select]');
      if (select) updateVariantState(select);
    }
  };

  const mountSlider = (root) => {
    const track = root.querySelector('[data-ayu-video-track]');
    if (!track || track.dataset.ayuSliderMounted === 'true') return;
    track.dataset.ayuSliderMounted = 'true';
    const cards = [...track.querySelectorAll('[data-ayu-video-card]')];
    const dots = root.querySelector('[data-ayu-video-dots]');

    const scrollByCard = (direction) => {
      const firstCard = track.querySelector('[data-ayu-video-card]');
      const distance = firstCard ? firstCard.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      track.scrollBy({ left: direction * distance, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };

    root.querySelector('[data-ayu-video-prev]')?.addEventListener('click', () => scrollByCard(-1));
    root.querySelector('[data-ayu-video-next]')?.addEventListener('click', () => scrollByCard(1));

    if (dots && cards.length > 1) {
      dots.innerHTML = '';
      cards.forEach((card, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `Go to video ${index + 1}`);
        button.addEventListener('click', () => {
          track.scrollTo({ left: card.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        });
        dots.appendChild(button);
      });

      const dotButtons = [...dots.querySelectorAll('button')];
      const updateDots = () => {
        const trackLeft = track.scrollLeft;
        let activeIndex = 0;
        let activeDistance = Infinity;
        cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - trackLeft);
          if (distance < activeDistance) {
            activeIndex = index;
            activeDistance = distance;
          }
        });
        dotButtons.forEach((button, index) => button.classList.toggle('is-active', index === activeIndex));
      };

      track.addEventListener('scroll', () => window.requestAnimationFrame(updateDots), { passive: true });
      updateDots();
    }
  };

  const mountVisibleVideoObserver = () => {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-ayu-video-host][data-load-mode="visible"]').forEach((host) => loadVideo(host));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        loadVideo(entry.target, entry.target.dataset.autoplay === 'true');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '360px 0px', threshold: 0.06 });

    document.querySelectorAll('[data-ayu-video-host][data-load-mode="visible"]').forEach((host) => observer.observe(host));
  };

  const mountFloatingVideo = (root) => {
    if (!root || root.dataset.ayuFloatingMounted === 'true') return;
    root.dataset.ayuFloatingMounted = 'true';
    const persistKey = `ayu-floating-video:${root.id}`;
    const desktopDefaultState = root.dataset.floatState || 'expanded';
    const mobileDefaultState = root.dataset.mobileFloatState || desktopDefaultState;
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    let previewStateTouched = false;

    const expandedHost = root.querySelector('.ayu-floating-video__expanded [data-ayu-video-host]');
    const bubbleHost = root.querySelector('.ayu-floating-video__bubble [data-ayu-video-host]');
    const dock = root.querySelector('.ayu-floating-video__dock');

    let offsetFrame = 0;
    const updateSmartOffset = () => {
      if (!dock || root.hidden || root.dataset.smartOffset === 'false') {
        root.style.setProperty('--ayu-float-smart-offset', '0px');
        return;
      }

      const dockRect = dock.getBoundingClientRect();
      const candidates = document.querySelectorAll([
        '[data-sticky-purchase]',
        '[data-sticky-cart]',
        '[data-bottom-navigation]',
        '[data-cro-proof]',
        '[class*="whatsapp"]',
        '[class*="floating"]',
        '[class*="sticky"]',
        'body > nav',
        'body > aside',
      ].join(','));
      let requiredOffset = 0;
      const clearance = Math.max(0, Number(root.dataset.smartOffsetGap) || 0);
      const maximumOffset = Math.max(0, Number(root.dataset.smartOffsetMax) || 0);

      candidates.forEach((candidate) => {
        if (!(candidate instanceof HTMLElement) || candidate === root || root.contains(candidate) || candidate.closest('[data-ayu-video-modal]')) return;
        const style = window.getComputedStyle(candidate);
        if ((style.position !== 'fixed' && style.position !== 'sticky') || style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return;
        const rect = candidate.getBoundingClientRect();
        if (rect.width < 24 || rect.height < 24 || rect.bottom < window.innerHeight - 8 || rect.top > window.innerHeight) return;
        const horizontalOverlap = rect.left < dockRect.right + 8 && rect.right > dockRect.left - 8;
        if (!horizontalOverlap) return;
        requiredOffset = Math.max(requiredOffset, Math.ceil(window.innerHeight - rect.top + clearance));
      });

      if (maximumOffset > 0) requiredOffset = Math.min(requiredOffset, maximumOffset);
      root.style.setProperty('--ayu-float-smart-offset', `${requiredOffset}px`);
    };

    const scheduleSmartOffset = () => {
      if (offsetFrame) return;
      offsetFrame = window.requestAnimationFrame(() => {
        offsetFrame = 0;
        updateSmartOffset();
      });
    };

    if (root.dataset.persistClose === 'true' && sessionStorage.getItem(persistKey) === 'closed') {
      root.dataset.floatState = 'closed';
      previewStateTouched = true;
    }

    const setState = (state) => {
      root.hidden = false;
      root.dataset.floatState = state;
      if (state === 'closed') {
        muteVideos(root);
        pauseInactiveVideos(root);
        scheduleSmartOffset();
        return;
      }

      if (state === 'expanded') {
        pauseInactiveVideos(root);
        loadVideo(expandedHost, expandedHost?.dataset.autoplay === 'true', { muted: true, scope: root });
      } else if (state === 'minimized') {
        muteVideos(root);
        pauseInactiveVideos(root);
        loadVideo(bubbleHost, true, { muted: true, scope: root });
      }
      scheduleSmartOffset();
    };

    const applyResponsiveDefault = () => {
      if (previewStateTouched) return;
      setState(mobileQuery.matches ? mobileDefaultState : desktopDefaultState);
    };

    applyResponsiveDefault();
    mobileQuery.addEventListener?.('change', applyResponsiveDefault);

    root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-ayu-float-minimize]')) {
        event.preventDefault();
        previewStateTouched = true;
        muteVideos(root);
        setState('minimized');
        return;
      }

      if (target.closest('[data-ayu-float-restore]')) {
        event.preventDefault();
        previewStateTouched = true;
        sessionStorage.removeItem(persistKey);
        setState('expanded');
        const video = loadVideo(expandedHost, true, { muted: false, scope: root });
        if (video) {
          setVideoMuted(video, false);
          video.play().catch(() => {
            setVideoMuted(video, true);
            setAudioControls(root, true);
          });
        }
        setAudioControls(root, video ? video.muted : false);
        return;
      }

      if (target.closest('[data-ayu-float-close]')) {
        event.preventDefault();
        previewStateTouched = true;
        muteVideos(root);
        setState('closed');
        if (root.dataset.persistClose === 'true') sessionStorage.setItem(persistKey, 'closed');
      }
    });

    window.addEventListener('resize', scheduleSmartOffset, { passive: true });
    window.addEventListener('scroll', scheduleSmartOffset, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(scheduleSmartOffset);
      observer.observe(document.documentElement);
      observer.observe(dock);
    }
    window.setTimeout(scheduleSmartOffset, 250);
    window.setTimeout(scheduleSmartOffset, 1200);
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openButton = target.closest('[data-ayu-video-open], [data-ayu-float-open]');
    if (openButton) {
      const root = findRoot(openButton);
      openModal(root, openButton.dataset.videoIndex || 0, { unmute: true });
      return;
    }

    const closeButton = target.closest('[data-ayu-video-close-modal]');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      const dialog = closeButton.closest('[data-ayu-video-modal]');
      if (dialog) closeModal(dialog);
      return;
    }

    const audioButton = target.closest('[data-ayu-video-audio-toggle]');
    if (audioButton) {
      toggleAudio(audioButton);
      return;
    }

    const buyNowButton = target.closest('[data-ayu-video-buy-now]');
    if (buyNowButton) {
      event.preventDefault();
      buyNow(buyNowButton);
      return;
    }

    const previousPanel = target.closest('[data-ayu-video-prev-panel]');
    const nextPanel = target.closest('[data-ayu-video-next-panel]');
    if (previousPanel || nextPanel) {
      const root = findRoot(target);
      const current = Number(root?.dataset.activeIndex || 0);
      activatePanel(root, current + (previousPanel ? -1 : 1));
    }
  });

  const primeIntentTarget = (target) => {
    const trigger = target.closest?.('[data-ayu-video-open], [data-ayu-float-open], [data-ayu-float-restore]');
    if (!trigger) return;
    const scope = trigger.closest('[data-ayu-video-card], .ayu-floating-video__expanded, .ayu-floating-video__bubble');
    const host = scope?.querySelector('[data-ayu-video-host]');
    primeImages(scope || trigger);
    if (host) loadVideo(host, false);
  };

  document.addEventListener('pointerover', (event) => primeIntentTarget(event.target), { passive: true });
  document.addEventListener('focusin', (event) => primeIntentTarget(event.target));

  document.addEventListener('cancel', (event) => {
    if (event.target?.matches?.('[data-ayu-video-modal]')) {
      closeModal(event.target);
    }
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target instanceof HTMLDialogElement ? event.target : null;
    if (!dialog?.matches('[data-ayu-video-modal]')) return;
    const rect = dialog.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) closeModal(dialog);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-ayu-video-variant-select]');
    if (select) updateVariantState(select);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('.ayu-video-product__form, .ayu-video-card__form');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  }, true);

  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-ayu-shoppable-root]').forEach(mountSlider);
    scope.querySelectorAll?.('[data-ayu-floating-video]').forEach(mountFloatingVideo);
    scope.querySelectorAll?.('[data-ayu-video-variant-select]').forEach(updateVariantState);
    mountVisibleVideoObserver();
    scope.querySelectorAll?.('[data-ayu-video-host][data-autoplay="true"][data-load-mode="click"]').forEach((host) => {
      if (host.closest('[data-ayu-video-modal]')) return;
      idle(() => loadVideo(host, true));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
